import * as THREE from 'three';
import { gsap } from 'gsap';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';

/**
 * BOOKEND VIDEO TRANSITION (2026-07-23 v2 — now composited IN WebGL). The user's smoke-burst
 * clip (2.68s, smoke on black) plays over the scene after the preloader and at the restart
 * loop; checkpoints keep the spiral shader.
 *
 * v1 was a DOM <video> with CSS mix-blend-mode:screen over the canvas. That compositing path
 * is not reliable across GPU/browser combos — where it fails, the clip's BLACK background
 * painted the whole screen black (the user's "black screen flash", seen even on good GPUs).
 * v2 samples the clip as a THREE.VideoTexture on a fullscreen clip-space quad and does the
 * keying in-shader — luminance IS the alpha, so black contributes nothing BY CONSTRUCTION,
 * on every device:
 *   - alpha = smoothstep(luma): dark pixels transparent, bright smoke opaque;
 *   - cover-fit UV mapping (16:9 clip on any viewport, centre-cropped);
 *   - uWhite: an in-shader white veil ramped around the clip's near-white peak, so the world
 *     swap underneath is guaranteed invisible (the measured peak is Y≈205/255, not solid);
 *   - uFade: master fade so the quad can never linger.
 *
 * The quad renders after everything (renderOrder above the CameraFog smoke), depth-ignoring,
 * and is excluded from the depth prepass + bloom traversal by Experience.
 */
export class VideoTransition {
  readonly group = new THREE.Group();
  private readonly video: HTMLVideoElement;
  private readonly material: THREE.ShaderMaterial;
  private readonly log = createLogger('video-transition');
  private failed = false;
  private playing = false;

  constructor() {
    const cfg = settings.videoTransition;

    this.video = document.createElement('video');
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.preload = 'auto';
    this.video.crossOrigin = 'anonymous';
    this.video.src = encodeURI(cfg.src);
    this.video.addEventListener('error', () => {
      this.failed = true;
      this.log.warn('transition video failed to load — bookends fall back to the timed reset.');
    });

    const texture = new THREE.VideoTexture(this.video);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uMap: { value: texture },
        uWhite: { value: 0 },
        uFade: { value: 1 },
        uViewAspect: { value: 16 / 9 },
        uVideoAspect: { value: 16 / 9 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        varying vec2 vUv;
        uniform sampler2D uMap;
        uniform float uWhite;
        uniform float uFade;
        uniform float uViewAspect;
        uniform float uVideoAspect;

        void main() {
          // COVER fit: fill the viewport, centre-crop the excess axis.
          vec2 uv = vUv - 0.5;
          if (uViewAspect > uVideoAspect) uv.y *= uVideoAspect / uViewAspect;
          else uv.x *= uViewAspect / uVideoAspect;
          uv += 0.5;

          vec3 smoke = texture2D(uMap, uv).rgb;
          // LUMINANCE KEY: the clip is smoke on black — brightness IS coverage. Black can
          // never paint the frame, whatever the device.
          float luma = dot(smoke, vec3(0.299, 0.587, 0.114));
          float alpha = smoothstep(0.03, 0.42, luma);

          // The white veil around the swap peak: whitens AND solidifies the frame.
          vec3 col = mix(smoke, vec3(1.0), uWhite);
          alpha = max(alpha, uWhite);

          gl_FragColor = vec4(col, alpha * uFade);
          #include <colorspace_fragment>
        }
      `,
    });

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    quad.frustumCulled = false;
    quad.renderOrder = 10_100; // above the CameraFog spiral smoke, above everything
    this.group.add(quad);
    this.group.name = 'video-transition';
    this.group.frustumCulled = false;
    this.group.visible = false;
  }

  /** False once the file errored — callers fall back to their non-video timing. */
  get available(): boolean {
    return settings.videoTransition.enabled && !this.failed;
  }

  /** True while the clip is on screen — Experience suspends quality adaptation then. */
  get isPlaying(): boolean {
    return this.playing;
  }

  /**
   * Play the smoke burst over the scene. `onCovered` (optional) fires under the white peak —
   * the one moment a world teleport is guaranteed invisible. Resolves once the clip has fully
   * dissipated and the quad is hidden again.
   */
  play(onCovered?: () => void): Promise<void> {
    const cfg = settings.videoTransition;
    if (!this.available || this.playing) {
      onCovered?.();
      return Promise.resolve();
    }
    this.playing = true;
    this.video.currentTime = 0;
    this.video.playbackRate = cfg.playbackRate;
    // All the clip-time marks below stretch by 1/playbackRate into real time.
    const timeScale = 1 / Math.max(cfg.playbackRate, 0.05);

    const u = this.material.uniforms;
    u.uFade!.value = 1;
    u.uWhite!.value = 0;
    u.uViewAspect!.value = window.innerWidth / Math.max(window.innerHeight, 1);
    this.group.visible = true;

    void this.video.play().catch((error: unknown) => {
      this.log.warn('transition video play() rejected — treating as unavailable.', error);
      this.failed = true;
    });

    return new Promise((resolve) => {
      // WHITE VEIL PULSE around the near-white peak: up just before, swap AT it, clear after.
      gsap.delayedCall(Math.max(cfg.coveredAtSec * timeScale - 0.28, 0), () => {
        gsap.to(u.uWhite!, { value: 1, duration: 0.26, ease: 'power2.out' });
      });
      gsap.delayedCall(cfg.coveredAtSec * timeScale, () => onCovered?.());
      gsap.delayedCall(cfg.coveredAtSec * timeScale + 0.24, () => {
        gsap.to(u.uWhite!, { value: 0, duration: 0.3, ease: 'power2.inOut' });
      });

      const finish = (): void => {
        if (!this.playing) return;
        this.playing = false;
        gsap.to(u.uFade!, {
          value: 0,
          duration: 0.3,
          ease: 'power2.out',
          onComplete: () => {
            this.group.visible = false;
            this.video.pause();
          },
        });
        resolve();
      };
      this.video.addEventListener('ended', finish, { once: true });
      // Safety net over a stalled decode — the experience must never hang on a transition.
      gsap.delayedCall(cfg.durationSec * timeScale + 0.6, finish);
    });
  }

  dispose(): void {
    (this.material.uniforms.uMap!.value as THREE.Texture)?.dispose();
    this.material.dispose();
    for (const child of this.group.children) (child as THREE.Mesh).geometry?.dispose();
    this.video.remove();
  }
}
